import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import data from '../data/data.json';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (d = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: d, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const projectIndex = data.projects.findIndex((p) => p.id === id);
  const project = data.projects[projectIndex];

  // Next project in list for continuous browsing
  const nextProject = useMemo(() => {
    if (projectIndex === -1) return null;
    const nextIdx = (projectIndex + 1) % data.projects.length;
    return data.projects[nextIdx];
  }, [projectIndex]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const handleBack = () => {
    navigate('/', { state: { scrollTo: 'projects' } });
  };

  if (!project) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '2.4rem', marginBottom: '1rem', color: 'var(--text)' }}>
            Case study not found
          </h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>
            The requested project details could not be located.
          </p>
          <button onClick={handleBack} className="btn btn-primary" data-cursor-hover>
            ← Back to All Projects
          </button>
        </div>
      </div>
    );
  }

  const { detail } = project;
  const isPDF = project.live && project.live.toLowerCase().endsWith('.pdf');
  const hasLive = project.live && project.live.trim() !== '';

  return (
    <div style={{ minHeight: '100vh', paddingTop: '5.5rem', paddingBottom: '7rem', position: 'relative' }}>
      <style>{`
        .case-study-hero-meta {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
          padding: 1.8rem;
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--border);
          border-radius: 20px;
          margin-bottom: 3.5rem;
          box-shadow: 0 6px 20px rgba(45, 23, 83, 0.04);
        }

        .meta-item-label {
          font-size: 0.74rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--accent-dark);
          margin-bottom: 0.35rem;
        }

        .meta-item-value {
          font-size: 0.95rem;
          font-weight: 600;
          color: var(--text);
        }

        .why-tech-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap: 1.25rem;
        }

        .media-container {
          border-radius: 18px;
          border: 1px solid var(--border);
          overflow: hidden;
          background: #ffffff;
          box-shadow: 0 12px 36px rgba(45, 23, 83, 0.08);
          margin-bottom: 2rem;
        }

        .window-chrome {
          padding: 0.65rem 1rem;
          background: rgba(45, 23, 83, 0.04);
          border-bottom: 1px solid var(--border);
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }

        .chrome-dot {
          width: 9px;
          height: 9px;
          border-radius: 50%;
          background: rgba(45, 23, 83, 0.18);
        }
      `}</style>

      <div className="container" style={{ maxWidth: '880px' }}>
        {/* Navigation bar with back button & live links */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={0}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '3rem',
            flexWrap: 'wrap',
            gap: '1rem',
          }}
        >
          <button
            onClick={handleBack}
            data-cursor-hover
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.88rem',
              fontWeight: 600,
              color: 'var(--text-muted)',
              padding: '0.45rem 1rem',
              borderRadius: '100px',
              background: 'rgba(255, 255, 255, 0.7)',
              border: '1px solid var(--border)',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--accent-dark)';
              e.currentTarget.style.borderColor = 'var(--accent)';
              e.currentTarget.style.transform = 'translateX(-3px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'var(--text-muted)';
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            ← All Case Studies
          </button>

          <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap' }}>
            {project.github && (
              <a href={project.github} target="_blank" rel="noreferrer" className="btn btn-outline" data-cursor-hover style={{ padding: '0.55rem 1.25rem', fontSize: '0.85rem' }}>
                GitHub ↗
              </a>
            )}
            {hasLive && (
              <a href={project.live} target="_blank" rel="noreferrer" className="btn btn-primary" data-cursor-hover style={{ padding: '0.55rem 1.25rem', fontSize: '0.85rem' }}>
                {isPDF ? '📄 View Case Study PDF ↗' : 'Live Demo ↗'}
              </a>
            )}
          </div>
        </motion.div>

        {/* Category badge */}
        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0.06}>
          <span
            style={{
              display: 'inline-block',
              fontSize: '0.78rem',
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'var(--accent-dark)',
              marginBottom: '0.85rem',
            }}
          >
            {project.category}
          </span>
        </motion.div>

        {/* Title */}
        <motion.h1
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={0.12}
          style={{
            fontFamily: 'var(--font-heading)',
            fontSize: 'clamp(2.4rem, 5vw, 3.6rem)',
            fontWeight: 700,
            lineHeight: 1.15,
            marginBottom: '1.25rem',
            letterSpacing: '-0.025em',
            color: 'var(--text)',
          }}
        >
          {project.title}
        </motion.h1>

        {/* Tagline */}
        <motion.p
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={0.18}
          style={{
            fontSize: '1.2rem',
            color: 'var(--text-muted)',
            marginBottom: '2.5rem',
            lineHeight: 1.65,
          }}
        >
          {project.tagline}
        </motion.p>

        {/* Metadata Grid Strip */}
        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0.24} className="case-study-hero-meta">
          <div>
            <div className="meta-item-label">Category</div>
            <div className="meta-item-value">{project.category}</div>
          </div>
          <div>
            <div className="meta-item-label">Role</div>
            <div className="meta-item-value">{detail.role ? 'Lead / Solo Developer' : 'Software Engineer'}</div>
          </div>
          <div>
            <div className="meta-item-label">Core Tech</div>
            <div className="meta-item-value">{project.tech.slice(0, 3).join(', ')}</div>
          </div>
          <div>
            <div className="meta-item-label">Repository</div>
            <div className="meta-item-value">
              {project.github ? 'Public Open Source' : 'Proprietary / Client'}
            </div>
          </div>
        </motion.div>

        {/* Tech Badges List */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          custom={0.3}
          style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '3.5rem' }}
        >
          {project.tech.map((t) => (
            <span
              key={t}
              style={{
                fontSize: '0.84rem',
                fontWeight: 500,
                padding: '0.35rem 0.9rem',
                borderRadius: '100px',
                background: 'rgba(255, 255, 255, 0.8)',
                border: '1px solid var(--border)',
                color: 'var(--text)',
              }}
            >
              {t}
            </span>
          ))}
        </motion.div>

        {/* Case Study Core Content */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '3.5rem' }}>
          {[
            { label: 'The Problem & Context', content: detail.problem, icon: '🎯' },
            { label: 'My Role & Responsibility', content: detail.role, icon: '👤' },
            { label: 'Methodology & Technical Approach', content: detail.approach, icon: '⚙️' },
            { label: 'Engineering Challenges & Solutions', content: detail.challenges, icon: '⚡' },
            { label: 'Results, Impact & Validation', content: detail.results, icon: '📊' },
          ].map((block, i) => (
            <motion.div
              key={block.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
              style={{
                marginBottom: '3rem',
                padding: '2rem',
                background: 'rgba(255, 255, 255, 0.65)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                borderRadius: '22px',
                border: '1px solid var(--border)',
                boxShadow: '0 4px 16px rgba(45, 23, 83, 0.03)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.85rem' }}>
                <span style={{ fontSize: '1.2rem' }}>{block.icon}</span>
                <h2
                  style={{
                    fontFamily: 'var(--font-heading)',
                    fontSize: '1.35rem',
                    fontWeight: 600,
                    color: 'var(--text)',
                  }}
                >
                  {block.label}
                </h2>
              </div>
              <p style={{ fontSize: '1rem', color: 'var(--text-muted)', lineHeight: 1.8 }}>
                {block.content}
              </p>
            </motion.div>
          ))}

          {/* Why Tech Choices Card Grid */}
          {detail.whyTech && Object.keys(detail.whyTech).length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              style={{ marginBottom: '3.5rem' }}
            >
              <h2
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '1.45rem',
                  fontWeight: 600,
                  marginBottom: '1.5rem',
                  color: 'var(--text)',
                }}
              >
                Technology Architecture — Why These Choices?
              </h2>
              <div className="why-tech-grid">
                {Object.entries(detail.whyTech).map(([tech, reason]) => (
                  <div
                    key={tech}
                    style={{
                      background: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(12px)',
                      borderRadius: '16px',
                      border: '1px solid var(--border)',
                      padding: '1.3rem',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.45rem',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        color: 'var(--accent-dark)',
                      }}
                    >
                      {tech}
                    </span>
                    <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                      {reason}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Screenshots & Video Demo Gallery */}
          {detail.screenshots && detail.screenshots.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55 }}
              style={{ marginBottom: '4rem' }}
            >
              <h2
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '1.45rem',
                  fontWeight: 600,
                  marginBottom: '1.5rem',
                  color: 'var(--text)',
                }}
              >
                Visual Demonstrations & Interfaces
              </h2>
              <div>
                {detail.screenshots.map((src, i) => {
                  const isVideo =
                    typeof src === 'string' &&
                    (src.endsWith('.mp4') || src.endsWith('.webm') || src.endsWith('.mov'));

                  return (
                    <div key={i} className="media-container">
                      <div className="window-chrome">
                        <div className="chrome-dot" style={{ background: '#ff5f56' }} />
                        <div className="chrome-dot" style={{ background: '#ffbd2e' }} />
                        <div className="chrome-dot" style={{ background: '#27c93f' }} />
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-faint)', marginLeft: '0.5rem' }}>
                          {project.title} — Demonstration {i + 1}
                        </span>
                      </div>
                      {isVideo ? (
                        <video controls style={{ width: '100%', display: 'block' }}>
                          <source src={src} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <img
                          src={src}
                          alt={`${project.title} view ${i + 1}`}
                          style={{ width: '100%', display: 'block' }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </div>

        {/* Next Project Continuous Explorer Card */}
        {nextProject && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{
              marginTop: '4rem',
              paddingTop: '3rem',
              borderTop: '1px solid var(--border)',
            }}
          >
            <span
              style={{
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--accent-dark)',
                display: 'block',
                marginBottom: '1rem',
              }}
            >
              Continue Exploring
            </span>
            <Link
              to={`/projects/${nextProject.id}`}
              data-cursor-hover
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '2rem 2.5rem',
                borderRadius: '24px',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(247, 190, 161, 0.2) 100%)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1.5px solid var(--border-accent)',
                boxShadow: '0 12px 36px rgba(45, 23, 83, 0.08)',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--accent)';
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 18px 45px rgba(232, 149, 109, 0.22)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-accent)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 12px 36px rgba(45, 23, 83, 0.08)';
              }}
            >
              <div>
                <span style={{ fontSize: '0.8rem', color: 'var(--accent-dark)', fontWeight: 600 }}>
                  Next Case Study →
                </span>
                <h3
                  style={{
                    fontFamily: 'var(--font-heading)',
                    fontSize: 'clamp(1.2rem, 2.5vw, 1.7rem)',
                    fontWeight: 600,
                    color: 'var(--text)',
                    marginTop: '0.3rem',
                  }}
                >
                  {nextProject.title}
                </h3>
              </div>
              <span style={{ fontSize: '1.8rem', color: 'var(--accent)', marginLeft: '1rem' }}>
                →
              </span>
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
}
